import {
  MOTION,
  withAlpha,
  type NoticeAppearanceColors,
  type ResolvedNoticeLayout,
} from "../shared/appearance";
import {
  BOTTOM_BAR_CONTENT_WIDTH,
  CORNER_OFFSET,
  NOTICE_KEYFRAMES,
  SHEET_PANEL_GAP,
  SOLID_SURFACE_DEFAULTS,
} from "./constants";
import type {
  AccordionStyles,
  CornerSide,
  LayoutParts,
  LayoutPartsInput,
  LayoutSurface,
  ModalPositionInput,
  Style,
  StyleRadii,
} from "./types";

/**
 * The surface an opaque style's layout parts draw on, from the admin's palette
 * over classic's own colours. Glass passes its translucent palette instead.
 */
export const solidSurface = (
  colors: NoticeAppearanceColors,
  accent: string,
  radii: StyleRadii,
): LayoutSurface => {
  const edge = colors.border ?? SOLID_SURFACE_DEFAULTS.edge;
  return {
    accent,
    panel: colors.surface ?? colors.background ?? SOLID_SURFACE_DEFAULTS.panel,
    panelEdge: edge,
    hairline: edge,
    outline: colors.toggle_off ?? SOLID_SURFACE_DEFAULTS.outline,
    footer: colors.background ?? SOLID_SURFACE_DEFAULTS.panel,
    accentWash: withAlpha(accent, 0.08),
    radii,
  };
};

const accordionStyles = (
  s: LayoutSurface,
  { isMobile, headingColor, textColor }: LayoutPartsInput,
): AccordionStyles => ({
  root: {
    borderRadius: s.radii.panel,
    backgroundColor: s.panel,
    border: `1px solid ${s.panelEdge}`,
    boxSizing: "border-box",
    flexShrink: 0,
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: isMobile ? "12px 14px" : "14px 16px",
    margin: 0,
    background: "none",
    border: 0,
    borderRadius: s.radii.panel,
    textAlign: "left",
    color: textColor,
    cursor: "pointer",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  icon: {
    display: "grid",
    placeItems: "center",
    width: "34px",
    height: "34px",
    borderRadius: s.radii.control,
    backgroundColor: s.accentWash,
    color: s.accent,
    flexShrink: 0,
  },
  text: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: 1.35,
    color: headingColor,
  },
  titleClamped: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    fontWeight: 600,
  },
  hint: {
    fontSize: "12px",
    lineHeight: 1.4,
    color: textColor,
    opacity: 0.75,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  chevron: { flexShrink: 0, opacity: 0.7 },
  body: {
    // The icon well's width plus its gap on desktop, so the copy lines up
    // with the title; phones need the width more than the alignment.
    padding: isMobile ? "0 14px 14px" : "0 16px 16px 62px",
    fontSize: isMobile ? "13px" : "14px",
    lineHeight: 1.55,
    color: textColor,
    boxSizing: "border-box",
  },
});

/**
 * The parts a layout choice draws with, in any style: the sheet's grabber and
 * bands, the collapsible sections and the paired phone footer.
 */
export const buildLayoutParts = (
  s: LayoutSurface,
  input: LayoutPartsInput,
): LayoutParts => {
  const { isMobile } = input;
  const gutter = isMobile ? "16px" : "24px";
  return {
    grabber: {
      width: "38px",
      height: "5px",
      margin: "8px auto 0",
      borderRadius: "999px",
      backgroundColor: s.outline,
      flexShrink: 0,
    },
    sheetContent: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      overflow: "hidden",
      minHeight: 0,
      margin: 0,
      boxSizing: "border-box",
      textAlign: "left",
    },
    sheetHeader: {
      padding: isMobile ? `12px ${gutter} 12px` : `22px ${gutter} 14px`,
      flexShrink: 0,
    },
    sheetGroup: {
      display: "flex",
      flexDirection: "column",
      gap: SHEET_PANEL_GAP,
    },
    sheetBody: {
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: SHEET_PANEL_GAP,
      flexGrow: 1,
      overflowY: "auto",
      overscrollBehavior: "contain",
      minHeight: 0,
      padding: isMobile ? "4px 16px 14px" : "4px 24px 18px",
      boxSizing: "border-box",
      textAlign: "left",
      animation: `${NOTICE_KEYFRAMES.settle} 520ms 160ms ${MOTION.outEase} both`,
    },
    footerBand: {
      padding: isMobile ? "12px 16px 16px" : "14px 24px 22px",
      borderTop: `1px solid ${s.hairline}`,
      background: s.footer,
      flexShrink: 0,
      boxSizing: "border-box",
    },
    actionButtonPair: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "8px",
      margin: 0,
      padding: 0,
    },
    accordion: accordionStyles(s, input),
  };
};

const RISE = `${NOTICE_KEYFRAMES.rise} ${MOTION.sheetMs}ms ${MOTION.sheetEase} both`;
const SETTLE = `${NOTICE_KEYFRAMES.settle} ${MOTION.dimMs}ms ${MOTION.outEase} both`;

/** Pinned to the bottom edge, full width, with only its top corners rounded. */
const edgeDock = (radius: string): Style => ({
  top: "auto",
  left: "0",
  right: "0",
  bottom: "0",
  transform: "none",
  width: "100%",
  maxWidth: "100%",
  borderRadius: `${radius} ${radius} 0 0`,
  borderBottom: "none",
  animation: RISE,
});

const corner = (side: CornerSide): Style => ({
  top: "auto",
  bottom: CORNER_OFFSET,
  left: side === "left" ? CORNER_OFFSET : "auto",
  right: side === "right" ? CORNER_OFFSET : "auto",
  transform: "none",
  animation: SETTLE,
});

/** Pinned to a viewport edge: a phone sheet, or a desktop bottom bar. */
export const isDocked = (
  layout: ResolvedNoticeLayout,
  isMobile: boolean,
): boolean =>
  isMobile ? layout.mobileSheet : layout.desktopPosition === "bottom-bar";

/**
 * Where the dialog sits, over whatever the style drew. Percentages, not
 * viewport units, so a frame the host renders the notice in is the
 * containing block.
 */
export const modalPosition = ({
  layout,
  isMobile,
  radius,
  maxWidth,
}: ModalPositionInput): Style => {
  if (isMobile) {
    return layout.mobileSheet
      ? {
          ...edgeDock(radius),
          maxHeight: "92vh",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }
      : {};
  }
  const width: Style = maxWidth
    ? { width: `min(${maxWidth}, calc(100% - 48px))`, maxWidth: "none" }
    : {};
  switch (layout.desktopPosition) {
    case "bottom-right":
      return { ...corner("right"), ...width };
    case "bottom-left":
      return { ...corner("left"), ...width };
    case "bottom-bar":
      return {
        ...edgeDock(radius),
        maxHeight: "70vh",
        paddingLeft: `max(0px, calc((100% - ${BOTTOM_BAR_CONTENT_WIDTH}) / 2))`,
        paddingRight: `max(0px, calc((100% - ${BOTTOM_BAR_CONTENT_WIDTH}) / 2))`,
      };
    default:
      return width;
  }
};
